/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { MailConfig, MailMode, MailService } from '@alfresco/aca-content';
import { EmailTemplate, EmailTemplateService } from './email-template.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/** A piece of document information that can be dropped into the text. */
interface Placeholder {
  token: string;
  label: string;
  sample: string;
  icon: string;
}

/** The file name the expiry job looks for. */
const EXPIRY_TEMPLATE = 'expiry-warning';

/**
 * Email templates (UI-16).
 *
 * The templates this application sends its own notifications from, kept under
 * "/Company Home/Email Templates". They are plain text with `{name}`-style
 * blanks, so the whole screen is built to be used the way one writes an email:
 * a subject, a message, and buttons that drop the document's details in where
 * the cursor is. Nothing here asks anyone to read or write code.
 */
@Component({
  selector: 'app-email-templates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './email-templates.component.html',
  styleUrl: './email-templates.component.scss'
})
export class EmailTemplatesComponent implements OnInit {
  /**
   * Whether the screen is shown inside the settings page rather than as a page
   * of its own — it then drops its own page header, because the page it sits in
   * already carries one.
   */
  @Input() embedded = false;

  private readonly translate = inject(TranslateService);
  private readonly service = inject(EmailTemplateService);
  private readonly notification = inject(NotificationService);
  private readonly sanitizer = inject(DomSanitizer);
  readonly mail = inject(MailService);

  @ViewChild('subjectField') private readonly subjectField?: ElementRef<HTMLInputElement>;
  @ViewChild('bodyField') private readonly bodyField?: ElementRef<HTMLTextAreaElement>;

  readonly templates = signal<EmailTemplate[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly selected = signal<EmailTemplate | null>(null);
  readonly dirty = signal(false);
  readonly creating = signal(false);

  /** The blanks the notification job fills in when it sends the mail. */
  readonly placeholders: Placeholder[] = [
    { token: '{name}', label: 'DMS.EMAIL_TEMPLATES.MAIL_PH_NAME', sample: 'DMS.EMAIL_TEMPLATES.MAIL_S_NAME', icon: 'description' },
    { token: '{expiryDate}', label: 'DMS.EMAIL_TEMPLATES.MAIL_PH_EXPIRY', sample: 'DMS.EMAIL_TEMPLATES.MAIL_S_EXPIRY', icon: 'event' },
    { token: '{path}', label: 'DMS.EMAIL_TEMPLATES.MAIL_PH_PATH', sample: 'DMS.EMAIL_TEMPLATES.MAIL_S_PATH', icon: 'folder' },
    { token: '{owner}', label: 'DMS.EMAIL_TEMPLATES.MAIL_PH_OWNER', sample: 'DMS.EMAIL_TEMPLATES.MAIL_S_OWNER', icon: 'person' },
    { token: '{modifier}', label: 'DMS.EMAIL_TEMPLATES.MAIL_PH_MODIFIER', sample: 'DMS.EMAIL_TEMPLATES.MAIL_S_MODIFIER', icon: 'edit' }
  ];

  search = '';

  // ── mail delivery settings ───────────────────────────────────────────────

  /** The fields of the delivery card — plain because ngModel writes to them. */
  mailMode: MailMode = 'mailto';
  mailFromName = '';
  ejServiceId = '';
  ejTemplateId = '';
  ejPublicKey = '';
  hookUrl = '';
  hookToken = '';

  readonly deliveryDirty = signal(false);
  readonly deliverySaving = signal(false);
  readonly deliveryTesting = signal(false);
  readonly deliveryTestResult = signal<'ok' | 'fail' | null>(null);
  /** Whether the sidecar already holds a webhook token — the field itself is write-only. */
  readonly hookTokenStored = signal(false);
  private deliveryFilled = false;

  constructor() {
    // Fill the delivery card when the stored configuration lands — load() is
    // asynchronous, so the first read after it resolves is the one that counts.
    // Once the administrator has touched the form it is never overwritten.
    effect(() => {
      const cfg = this.mail.settings();
      if (!this.deliveryFilled && !this.deliveryDirty()) {
        this.mailMode = cfg.mode;
        this.mailFromName = cfg.fromName;
        this.ejServiceId = cfg.emailjs.serviceId;
        this.ejTemplateId = cfg.emailjs.templateId;
        this.ejPublicKey = cfg.emailjs.publicKey;
        this.hookUrl = cfg.webhook.url;
        this.deliveryFilled = cfg !== MailService.DEFAULT;
      }
    });
  }

  /** The fields being edited. Plain fields because ngModel writes to them. */
  subject = '';
  body = '';
  newName = '';

  /** Which field the cursor was last in, so a chip knows where to go. */
  private focusedField: 'subject' | 'body' = 'body';

  ngOnInit(): void {
    this.load();
    this.mail.load();
    this.refreshHookToken();
  }

  private refreshHookToken(): void {
    this.mail.webhookStatus().subscribe((status) => this.hookTokenStored.set(!!status?.hasToken));
  }

  deliveryChanged(): void {
    this.deliveryDirty.set(true);
    this.deliveryTestResult.set(null);
  }

  deliveryConfig(): MailConfig {
    return {
      mode: this.mailMode,
      fromName: this.mailFromName,
      emailjs: { serviceId: this.ejServiceId, templateId: this.ejTemplateId, publicKey: this.ejPublicKey },
      webhook: { url: this.hookUrl, token: this.hookToken }
    };
  }

  /** Whether the chosen channel has everything it needs to send. */
  deliveryReady(): boolean {
    return MailService.isConfigured(this.deliveryConfig());
  }

  saveDelivery(): void {
    this.deliverySaving.set(true);
    this.mail.save(this.deliveryConfig()).subscribe({
      next: () => {
        this.deliverySaving.set(false);
        this.deliveryDirty.set(false);
        // the token went to the sidecar; the field only ever takes a new one
        this.hookToken = '';
        this.refreshHookToken();
        this.notification.showInfo(this.translate.instant('DMS.MAIL.SAVED'));
      },
      error: () => {
        this.deliverySaving.set(false);
        this.notification.showError(this.translate.instant('DMS.MAIL.SAVE_FAILED'));
      }
    });
  }

  /** Sends a short test message to the signed-in person's own address. */
  testDelivery(): void {
    if (this.mailMode === 'mailto') {
      const subject = encodeURIComponent(this.translate.instant('DMS.MAIL.TEST_SUBJECT'));
      const body = encodeURIComponent(this.translate.instant('DMS.MAIL.TEST_BODY'));
      window.location.href = `mailto:${encodeURIComponent(this.mail.viewer().email)}?subject=${subject}&body=${body}`;
      this.deliveryTestResult.set('ok');
      return;
    }
    // the sidecar sends to the webhook it has stored, not to what is typed here
    if (this.mailMode === 'webhook' && this.deliveryDirty()) {
      this.notification.showWarning(this.translate.instant('DMS.MAIL.SAVE_FIRST'));
      return;
    }
    this.deliveryTesting.set(true);
    this.deliveryTestResult.set(null);
    this.mail
      .send(
        {
          to: this.mail.viewer().email,
          subject: this.translate.instant('DMS.MAIL.TEST_SUBJECT'),
          body: this.translate.instant('DMS.MAIL.TEST_BODY')
        },
        this.deliveryConfig()
      )
      .subscribe({
        next: () => {
          this.deliveryTesting.set(false);
          this.deliveryTestResult.set('ok');
          this.notification.showInfo(this.translate.instant('DMS.MAIL.TEST_SENT'));
        },
        error: () => {
          this.deliveryTesting.set(false);
          this.deliveryTestResult.set('fail');
          this.notification.showError(this.translate.instant('DMS.MAIL.TEST_FAILED'));
        }
      });
  }

  /** The test mail goes to the signed-in person — show where it will land. */
  testTarget(): string {
    return this.mail.viewer().email || '—';
  }

  load(): void {
    this.loading.set(true);
    this.service.list().subscribe({
      next: (templates) => {
        this.templates.set(templates);
        this.loading.set(false);
        if (!this.selected() && !this.creating() && templates.length) {
          this.select(templates[0]);
        }
      },
      error: () => {
        this.loading.set(false);
        this.notification.showError(this.translate.instant('DMS.UI.AC38FA'));
      }
    });
  }

  // ── the list ───────────────────────────────────────────────────────────────

  filtered(): EmailTemplate[] {
    const term = this.search.trim().toLowerCase();
    return this.templates().filter((template) => !term || `${template.name} ${template.subject}`.toLowerCase().includes(term));
  }

  /**
   * The name to show a reader. The one template the expiry job looks for has a
   * name of its own, so nobody has to recognise it by its file name or by a
   * subject line still full of blanks; anything else goes by its subject.
   */
  displayName(template: EmailTemplate): string {
    if (template.name.startsWith(EXPIRY_TEMPLATE)) {
      return this.translate.instant('DMS.EMAIL_TEMPLATES.MAIL_KNOWN_EXPIRY');
    }
    return template.subject.trim() || template.name;
  }

  iconOf(template: EmailTemplate): string {
    return template.name.startsWith(EXPIRY_TEMPLATE) ? 'schedule_send' : 'mail';
  }

  select(template: EmailTemplate): void {
    this.selected.set(template);
    this.creating.set(false);
    this.subject = template.subject;
    this.body = '';
    this.dirty.set(false);
    this.service.getBody(template.id).subscribe({
      next: (text) => {
        this.body = text;
        this.dirty.set(false);
      },
      error: () => this.notification.showError(this.translate.instant('DMS.UI.728B1A'))
    });
  }

  touched(): void {
    this.dirty.set(true);
  }

  // ── writing the text ───────────────────────────────────────────────────────

  remember(field: 'subject' | 'body'): void {
    this.focusedField = field;
  }

  /**
   * Drops a placeholder in where the cursor is, so nobody has to type the
   * braces themselves.
   */
  insert(placeholder: Placeholder): void {
    const onSubject = this.focusedField === 'subject';
    const element = onSubject ? this.subjectField?.nativeElement : this.bodyField?.nativeElement;
    const current = onSubject ? this.subject : this.body;
    const start = element?.selectionStart ?? current.length;
    const end = element?.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${placeholder.token}${current.slice(end)}`;

    if (onSubject) {
      this.subject = next;
    } else {
      this.body = next;
    }
    this.dirty.set(true);

    if (element) {
      const caret = start + placeholder.token.length;
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(caret, caret);
      });
    }
  }

  /** Whether a placeholder is already used somewhere in the template. */
  usesPlaceholder(placeholder: Placeholder): boolean {
    return `${this.subject}${this.body}`.includes(placeholder.token);
  }

  // ── the preview ────────────────────────────────────────────────────────────

  /** The subject as the recipient would read it, sample values filled in. */
  previewSubject(): SafeHtml {
    const html = this.fillIn(this.subject) || this.escape(this.translate.instant('DMS.EMAIL_TEMPLATES.MAIL_NO_SUBJECT'));
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /** The body as the recipient would read it, sample values filled in. */
  previewBody(): SafeHtml {
    const html = this.fillIn(this.body) || this.escape(this.translate.instant('DMS.EMAIL_TEMPLATES.MAIL_NO_BODY'));
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Escapes the text, then swaps each placeholder for a highlighted sample of
   * what the notification job would put there. Escaping first is what makes the
   * result safe to hand to `innerHTML`.
   */
  private fillIn(text: string): string {
    if (!text.trim()) {
      return '';
    }
    let html = this.escape(text);
    for (const placeholder of this.placeholders) {
      const sample = this.escape(this.translate.instant(placeholder.sample));
      html = html.split(this.escape(placeholder.token)).join(`<mark class="aca-mail__sample">${sample}</mark>`);
    }
    return html.replace(/\r?\n/g, '<br />');
  }

  private escape(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── creating, saving and deleting ──────────────────────────────────────────

  startCreate(): void {
    this.creating.set(true);
    this.selected.set(null);
    this.newName = '';
    this.subject = '';
    this.body = '';
    this.dirty.set(false);
  }

  /** Offers the expiry warning already written out, ready to be adjusted. */
  startExpiryTemplate(): void {
    this.creating.set(true);
    this.selected.set(null);
    this.newName = EXPIRY_TEMPLATE;
    this.subject = this.translate.instant('DMS.EMAIL_TEMPLATES.MAIL_DEFAULT_SUBJECT');
    this.body = this.translate.instant('DMS.EMAIL_TEMPLATES.MAIL_DEFAULT_BODY');
    this.dirty.set(true);
  }

  cancelCreate(): void {
    this.creating.set(false);
    const first = this.filtered()[0];
    if (first) {
      this.select(first);
    } else {
      this.subject = '';
      this.body = '';
    }
  }

  /** The file name, kept to what Alfresco accepts without anyone thinking about it. */
  private safeName(): string {
    const cleaned = this.newName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return cleaned || EXPIRY_TEMPLATE;
  }

  canSave(): boolean {
    if (this.saving()) {
      return false;
    }
    if (this.creating()) {
      return !!this.newName.trim() && !!this.subject.trim() && !!this.body.trim();
    }
    return !!this.selected();
  }

  save(): void {
    if (!this.canSave()) {
      return;
    }
    this.saving.set(true);

    if (this.creating()) {
      const name = this.safeName();
      this.service.create(name, this.subject, this.body).subscribe({
        next: () => {
          this.saving.set(false);
          this.creating.set(false);
          this.notification.showInfo(this.translate.instant('DMS.UI.B15F91'));
          this.reload(name);
        },
        error: () => {
          this.saving.set(false);
          this.notification.showError(this.translate.instant('DMS.UI.27DEED'));
        }
      });
      return;
    }

    const template = this.selected();
    this.service.update(template.id, this.subject, this.body).subscribe({
      next: () => {
        this.saving.set(false);
        this.dirty.set(false);
        template.subject = this.subject;
        this.notification.showInfo(this.translate.instant('DMS.UI.C6666F', { p0: this.displayName(template) }));
      },
      error: () => {
        this.saving.set(false);
        this.notification.showError(this.translate.instant('DMS.UI.27DEED'));
      }
    });
  }

  remove(): void {
    const template = this.selected();
    if (!template) {
      return;
    }
    if (!confirm(this.translate.instant('DMS.EMAIL_TEMPLATES.MAIL_DELETE_CONFIRM', { p0: this.displayName(template) }))) {
      return;
    }
    this.saving.set(true);
    this.service.delete(template.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.selected.set(null);
        this.notification.showInfo(this.translate.instant('DMS.EMAIL_TEMPLATES.MAIL_DELETED'));
        this.reload();
      },
      error: () => {
        this.saving.set(false);
        this.notification.showError(this.translate.instant('DMS.UI.582420'));
      }
    });
  }

  /** Reloads the list, selecting one by name if asked. */
  private reload(selectName?: string): void {
    this.service.list().subscribe((templates) => {
      this.templates.set(templates);
      const wanted = selectName ? templates.find((template) => template.name === selectName) : templates[0];
      if (wanted) {
        this.select(wanted);
      } else {
        this.selected.set(null);
        this.subject = '';
        this.body = '';
      }
    });
  }

  trackById(_index: number, template: EmailTemplate): string {
    return template.id;
  }
}
